// UserSettings service that integrates with the keystore API
// Uses https://dcps-core-api-dev.azurewebsites.net/api/profile/settings

import { profileService } from '../util/Services'
import { isServerErrorResponse } from '../adapters/ApiSchema'

export interface UserSettings {
  user: {
    beta_site_opt_in: boolean
  }
}

export const DEFAULT_USER_SETTINGS: UserSettings = {
  user: {
    beta_site_opt_in: false,
  },
}

export class UserSettingsService {
  private static instance: UserSettingsService
  private cache: UserSettings | null = null
  private cacheTimestamp: number = 0
  private readonly CACHE_DURATION = 5 * 60 * 1000 // 5 minutes

  static getInstance(): UserSettingsService {
    if (!UserSettingsService.instance) {
      UserSettingsService.instance = new UserSettingsService()
    }
    return UserSettingsService.instance
  }

  /**
   * Get user settings from the keystore API
   */
  async getUserSettings(): Promise<UserSettings> {
    // Check cache first
    if (this.cache && Date.now() - this.cacheTimestamp < this.CACHE_DURATION) {
      return this.cache
    }

    try {
      // Use the public getProfileSettings method
      const response = await profileService.getProfileSettings()

      if (!isServerErrorResponse(response)) {
        // Extract user_beta_site_opt_in from the profile settings response
        const betaOptIn = (response as any)?.user_beta_site_opt_in ?? false

        this.cache = {
          user: {
            beta_site_opt_in: betaOptIn,
          },
        }

        if (process.env.NODE_ENV === 'development') {
          console.log('[UserSettingsService] Settings loaded from API:', this.cache)
        }
      } else {
        console.warn('[UserSettingsService] API error, using defaults:', response)
        this.cache = { ...DEFAULT_USER_SETTINGS }
      }
    } catch (error) {
      console.error('[UserSettingsService] Error loading settings:', error)
      this.cache = { ...DEFAULT_USER_SETTINGS }
    }

    this.cacheTimestamp = Date.now()
    return this.cache
  }

  /**
   * Update user settings via the keystore API using POST with flat key-value format
   */
  async updateUserSettings(updates: Partial<UserSettings>): Promise<UserSettings> {
    try {
      const currentSettings = await this.getUserSettings()
      const updatedSettings = this.deepMerge(currentSettings, updates)

      // Prepare the API payload using flat key-value format like theme system
      const apiPayload = {
        user_beta_site_opt_in: updatedSettings.user.beta_site_opt_in,
      }

      const response = await profileService.updateProfileSettings(apiPayload)

      if (!isServerErrorResponse(response)) {
        // Update cache with the new settings
        this.cache = updatedSettings
        this.cacheTimestamp = Date.now()

        // Dispatch custom event for other components to listen to
        window.dispatchEvent(
          new CustomEvent('user-settings-changed', {
            detail: { settings: updatedSettings },
          })
        )

        if (process.env.NODE_ENV === 'development') {
          console.log('[UserSettingsService] Settings updated via API:', updatedSettings)
        }

        return updatedSettings
      } else {
        console.error('[UserSettingsService] API update failed:', response)
        throw new Error('Failed to update settings via API')
      }
    } catch (error) {
      console.error('[UserSettingsService] Error updating settings:', error)
      throw error
    }
  }

  /**
   * Update a specific category of settings
   */
  async updateSettingCategory<K extends keyof UserSettings>(
    category: K,
    updates: Partial<UserSettings[K]>
  ): Promise<UserSettings> {
    const categoryUpdate = { [category]: updates } as Partial<UserSettings>
    return this.updateUserSettings(categoryUpdate)
  }

  /**
   * Clear cache to force refresh from API
   */
  clearCache(): void {
    this.cache = null
    this.cacheTimestamp = 0
  }

  /**
   * Deep merge utility
   */
  private deepMerge(target: any, source: any): any {
    const result = { ...target }

    for (const key in source) {
      if (source[key] && typeof source[key] === 'object' && !Array.isArray(source[key])) {
        result[key] = this.deepMerge(target[key] || {}, source[key])
      } else {
        result[key] = source[key]
      }
    }

    return result
  }
}

// Export singleton instance
export const userSettingsService = UserSettingsService.getInstance()
